import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useState } from 'react';
import {
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { useGetDbosWorkflows } from '../hooks/useDbosData';
import type {
    DbosWorkflowListItem,
    DbosWorkflowStatus,
} from '../types/DbosTypes';
import { DBOS_STATUS_COLORS, formatEpochMs } from './dbosConstants';

interface DbosWorkflowExplorerProps {
    startTime?: string;
    endTime?: string;
    onSelectWorkflow: (workflowId: string) => void;
}

const STATUS_OPTIONS = [
    { label: 'All Statuses', value: undefined },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Success', value: 'SUCCESS' },
    { label: 'Error', value: 'ERROR' },
    { label: 'Max Recovery', value: 'MAX_RECOVERY_ATTEMPTS_EXCEEDED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'Enqueued', value: 'ENQUEUED' },
];

const PAGE_SIZE = 50;

export const DbosWorkflowExplorer = ({
    startTime,
    endTime,
    onSelectWorkflow,
}: DbosWorkflowExplorerProps) => {
    const [statusFilter, setStatusFilter] = useState<
        DbosWorkflowStatus | undefined
    >(undefined);
    const [workflowNameFilter, setWorkflowNameFilter] = useState('');
    const [offset, setOffset] = useState(0);

    const { data, isLoading, error } = useGetDbosWorkflows(
        {
            status: statusFilter,
            workflowName: workflowNameFilter || undefined,
            startTime,
            endTime,
            limit: PAGE_SIZE,
            offset,
            sortDesc: true,
        },
        {
            staleTime: 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    );

    const workflows = data ?? [];

    const statusBodyTemplate = (rowData: DbosWorkflowListItem) => {
        const color = DBOS_STATUS_COLORS[rowData.status] ?? '#333';
        return (
            <span
                style={{
                    color,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                }}
            >
                {rowData.status}
            </span>
        );
    };

    const handlePrevPage = () => {
        setOffset(Math.max(0, offset - PAGE_SIZE));
    };

    const handleNextPage = () => {
        if (workflows.length === PAGE_SIZE) {
            setOffset(offset + PAGE_SIZE);
        }
    };

    return (
        <YStack gap={12} width="100%">
            <H6Emphasized>Workflow Explorer</H6Emphasized>

            {/* Filters */}
            <XStack gap={8} alignItems="center" flexWrap="wrap">
                <Dropdown
                    value={statusFilter}
                    options={STATUS_OPTIONS}
                    onChange={(e) => {
                        setStatusFilter(
                            e.value as DbosWorkflowStatus | undefined,
                        );
                        setOffset(0);
                    }}
                    placeholder="All Statuses"
                    style={{ fontSize: '0.8rem', minWidth: 160 }}
                />
                <InputText
                    value={workflowNameFilter}
                    onChange={(e) => {
                        setWorkflowNameFilter(e.target.value);
                        setOffset(0);
                    }}
                    placeholder="Filter by workflow name..."
                    style={{ fontSize: '0.8rem', minWidth: 200 }}
                />
                <BodySmallRegular color="$gray1000">
                    Showing {offset + 1}-{offset + workflows.length}
                </BodySmallRegular>
            </XStack>

            {/* Table */}
            {isLoading ? (
                <Spinner />
            ) : error != null ? (
                <BodySmallRegular color="$red1100">
                    Error loading workflows:{' '}
                    {error instanceof Error ? error.message : String(error)}
                </BodySmallRegular>
            ) : (
                <>
                    <DataTable
                        value={workflows}
                        size="small"
                        tableStyle={{
                            width: '100%',
                            fontSize: '0.875rem',
                        }}
                        emptyMessage="No workflows found"
                        selectionMode="single"
                        onRowClick={(e) => {
                            const row = e.data as DbosWorkflowListItem;
                            onSelectWorkflow(row.workflowID);
                        }}
                    >
                        <Column
                            field="workflowID"
                            header="Workflow ID"
                            style={{
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                            }}
                            body={(row: DbosWorkflowListItem) => (
                                <span
                                    style={{
                                        fontSize: '0.7rem',
                                        fontFamily: 'monospace',
                                        cursor: 'pointer',
                                        color: '#1565c0',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {row.workflowID.length > 30
                                        ? `${row.workflowID.substring(
                                              0,
                                              30,
                                          )}...`
                                        : row.workflowID}
                                </span>
                            )}
                        />
                        <Column
                            field="status"
                            header="Status"
                            body={statusBodyTemplate}
                        />
                        <Column
                            field="workflowName"
                            header="Workflow"
                            style={{ fontSize: '0.75rem' }}
                        />
                        <Column
                            field="workflowClassName"
                            header="Class"
                            style={{ fontSize: '0.75rem' }}
                        />
                        <Column
                            field="workflowConfigName"
                            header="Config"
                            style={{ fontSize: '0.75rem' }}
                        />
                        <Column
                            field="queueName"
                            header="Queue"
                            style={{ fontSize: '0.75rem' }}
                            body={(row: DbosWorkflowListItem) => (
                                <span style={{ fontSize: '0.75rem' }}>
                                    {row.queueName ?? '-'}
                                </span>
                            )}
                        />
                        <Column
                            header="Created"
                            style={{ fontSize: '0.7rem' }}
                            body={(row: DbosWorkflowListItem) => (
                                <span style={{ fontSize: '0.7rem' }}>
                                    {formatEpochMs(row.createdAt)}
                                </span>
                            )}
                            sortable
                            sortField="createdAt"
                        />
                        <Column
                            header="Updated"
                            style={{ fontSize: '0.7rem' }}
                            body={(row: DbosWorkflowListItem) => (
                                <span style={{ fontSize: '0.7rem' }}>
                                    {formatEpochMs(row.updatedAt)}
                                </span>
                            )}
                        />
                        <Column
                            field="recoveryAttempts"
                            header="Recovery"
                            alignHeader="right"
                            bodyStyle={{ textAlign: 'right' }}
                            body={(row: DbosWorkflowListItem) => (
                                <span
                                    style={{
                                        fontWeight:
                                            row.recoveryAttempts > 0
                                                ? 'bold'
                                                : 'normal',
                                        color:
                                            row.recoveryAttempts > 0
                                                ? '#e65100'
                                                : '#333',
                                    }}
                                >
                                    {row.recoveryAttempts}
                                </span>
                            )}
                        />
                    </DataTable>

                    {/* Pagination */}
                    <XStack
                        justifyContent="flex-end"
                        gap={8}
                        alignItems="center"
                    >
                        <Button
                            variant="tertiary"
                            size="small"
                            disabled={offset === 0}
                            onPress={handlePrevPage}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="tertiary"
                            size="small"
                            disabled={workflows.length < PAGE_SIZE}
                            onPress={handleNextPage}
                        >
                            Next
                        </Button>
                    </XStack>
                </>
            )}

            <style>{`
                .p-datatable tbody tr {
                    cursor: pointer;
                }
                .p-datatable tbody tr:hover {
                    background-color: #e3f2fd !important;
                }
            `}</style>
        </YStack>
    );
};
